import React, { useState, useEffect } from 'react';
import { Wallet, Clock, XCircle, Hourglass, Activity, Upload, CheckCircle2, Layers, ChevronUp, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `¥${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const EVENT = {
  uploaded: { label: 'Receipt uploaded', icon: Upload, cls: 'text-sky-700 bg-sky-50 border-sky-200' },
  verified: { label: 'Payment verified', icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  rejected: { label: 'Receipt rejected', icon: XCircle, cls: 'text-red-700 bg-red-50 border-red-200' },
};

function Card({ label, value, sub, icon: Icon, cls, testId }) {
  return (
    <div className={`rounded-xl border p-4 ${cls}`} data-testid={testId}>
      <div className="flex items-center justify-between"><span className="text-[11px] uppercase font-bold tracking-wide opacity-70">{label}</span><Icon className="w-4 h-4 opacity-60" /></div>
      <div className="text-xl font-bold mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function PaymentDashboard({ token, refreshKey, onSelectOrder }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/payments/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()).then((d) => d.success && setData(d.data)).catch(() => {});
  }, [token, refreshKey]);

  if (!data) return null;
  const v = data.verified;
  const recent = showAll ? data.recent : data.recent.slice(0, 6);
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6" data-testid="payment-dashboard">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5 text-left" data-testid="payment-dashboard-toggle">
        <span className="font-bold text-gray-900 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" /> Payments
          <span className="text-xs font-normal text-gray-500 hidden sm:inline">— Stanbic CIPS receipts & verified RMB</span>
          {data.pending.receipts > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">{data.pending.receipts} to verify</span>}
        </span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && (
      <div className="px-5 pb-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card label="Verified today" value={money(v.today.rmb)} sub={`${v.today.count} payment${v.today.count === 1 ? '' : 's'}`} icon={CheckCircle2} cls="text-emerald-800 bg-emerald-50 border-emerald-200" testId="pay-stat-today" />
        <Card label="Last 7 days" value={money(v['7d'].rmb)} sub={`${v['7d'].count} payment${v['7d'].count === 1 ? '' : 's'}`} icon={CheckCircle2} cls="text-emerald-800 bg-emerald-50 border-emerald-200" testId="pay-stat-7d" />
        <Card label="Last 30 days" value={money(v['30d'].rmb)} sub={`${v['30d'].count} payment${v['30d'].count === 1 ? '' : 's'}`} icon={CheckCircle2} cls="text-emerald-800 bg-emerald-50 border-emerald-200" testId="pay-stat-30d" />
        <Card label="Pending receipts" value={data.pending.receipts} sub={`${money(data.pending.rmb)} across ${data.pending.orders} order${data.pending.orders === 1 ? '' : 's'}${data.pending.batches ? ` · ${data.pending.batches} combined` : ''}`} icon={Clock} cls="text-amber-800 bg-amber-50 border-amber-200" testId="pay-stat-pending" />
        <Card label="Rejected (30d)" value={data.rejected_30d} sub="receipts sent back" icon={XCircle} cls="text-red-800 bg-red-50 border-red-200" testId="pay-stat-rejected" />
        <Card label="Avg. verification" value={data.avg_verification_hours == null ? '—' : `${data.avg_verification_hours} h`} sub="upload → approval (30d)" icon={Hourglass} cls="text-blue-800 bg-blue-50 border-blue-200" testId="pay-stat-avg" />
      </div>

      <div className="mt-5">
        <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1 mb-2"><Activity className="w-3.5 h-3.5" /> Recent payment activity</p>
        {data.recent.length === 0 ? <p className="text-sm text-gray-400">No payment activity yet.</p> : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg" data-testid="pay-recent-list">
            {recent.map((ev) => { const m = EVENT[ev.type] || EVENT.uploaded; const Icon = m.icon; return (
              <li key={ev.id} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs" data-testid={`pay-event-${ev.id}`}>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border font-semibold whitespace-nowrap ${m.cls}`}><Icon className="w-3 h-3" /> {m.label}</span>
                {ev.batch_id && <span className="inline-flex items-center gap-1 text-violet-700"><Layers className="w-3 h-3" /> combined</span>}
                <span className="font-mono text-gray-700">{ev.reference}</span>
                <span className="text-gray-500 truncate">{ev.client_name}</span>
                <span className="flex gap-1 flex-wrap">{ev.order_ids.map((id) => <button key={id} type="button" onClick={() => onSelectOrder?.(id)} className="text-blue-600 hover:underline font-mono" data-testid={`pay-event-order-${ev.id}-${id}`}>#{id}</button>)}</span>
                <span className="font-semibold text-gray-800 ml-auto whitespace-nowrap">{money(ev.amount_rmb)}</span>
                <span className="text-gray-400 whitespace-nowrap">{new Date(ev.timestamp).toLocaleString()}</span>
                {ev.note && <span className="w-full sm:w-auto text-gray-400 truncate">— {ev.note}</span>}
              </li>
            ); })}
          </ul>
        )}
        {data.recent.length > 6 && (
          <button type="button" onClick={() => setShowAll((s) => !s)} className="mt-2 text-xs font-semibold text-blue-600 hover:underline" data-testid="pay-recent-toggle">{showAll ? 'Show less' : `Show all ${data.recent.length}`}</button>
        )}
      </div>
      </div>
      )}
    </div>
  );
}
